import { App } from './app/app';
import { AuthScreen } from './ui/authScreen';
import { Dashboard } from './ui/dashboard';
import { api, ApiError } from './io/api';
import type { ProjectRecord } from './io/api';

/**
 * Mini-Router (keine Abhängigkeit):
 *   `/`         → sofort zeichnen (anonymer Entwurf); eingeloggt: Projektübersicht
 *   `/join`     → anonymer Editor (Kollaboration per ?room=, ohne Konto)
 *   `/p/:id`    → Editor mit gebundenem Server-Projekt, hinter Login
 *
 * Speichern aus dem Entwurf heraus öffnet den Konto-Dialog (E-Mail + Passwort)
 * und legt dann das Projekt an — zeichnen selbst braucht kein Konto.
 * Alte Links (`/?room=…`) funktionieren weiter: der Entwurfs-Editor liest den
 * room-Parameter selbst; eingeloggt leitet `/` mit ?room= auf /join um.
 */

const PROJECT_RE = /^\/p\/([A-Za-z0-9_-]{4,64})\/?$/;

export function bootstrap(root: HTMLElement): void {
  const path = location.pathname.replace(/\/+$/, '') || '/';
  const room = new URLSearchParams(location.search).get('room');

  if (path === '/') {
    void renderHome(root, room);
    return;
  }

  if (path === '/join') {
    window.campsketch = new App(root, { project: null });
    return;
  }

  const match = PROJECT_RE.exec(path);
  if (match) {
    void renderEditor(root, match[1]!);
    return;
  }

  location.replace('/');
}

/** `/`: eingeloggt → Projektübersicht, sonst direkt in den Entwurfs-Editor. */
async function renderHome(root: HTMLElement, room: string | null): Promise<void> {
  const user = await safeMe();
  if (user) {
    if (room) {
      location.replace(`/join?room=${encodeURIComponent(room)}`);
      return;
    }
    new Dashboard(root, user);
    return;
  }
  // Anonym: sofort zeichnen (entweder ?room beitreten oder lokaler Entwurf)
  window.campsketch = new App(root, { project: null });
}

async function renderEditor(root: HTMLElement, projectId: string): Promise<void> {
  const user = await safeMe();
  if (!user) {
    new AuthScreen(root, {
      message: 'Zum Öffnen des Projekts bitte anmelden.',
      onSuccess: () => void renderEditor(root, projectId)
    });
    return;
  }
  let record: ProjectRecord;
  try {
    record = (await api.getProject(projectId)).project;
  } catch (err) {
    renderLoadError(root, err instanceof ApiError && err.status === 404 ? 'Dieses Projekt existiert nicht (mehr) oder gehört einem anderen Konto.' : 'Projekt konnte nicht geladen werden — Server nicht erreichbar?');
    console.error(err);
    return;
  }
  window.campsketch = new App(root, { project: { id: record.id, name: record.name }, data: record.data });
}

async function safeMe() {
  try {
    return await api.me();
  } catch {
    return null;
  }
}

function renderLoadError(root: HTMLElement, message: string): void {
  root.replaceChildren();
  root.className = 'auth-screen';
  const card = document.createElement('div');
  card.className = 'auth-card';
  const h1 = document.createElement('h1');
  h1.textContent = 'Projekt nicht gefunden';
  const p = document.createElement('p');
  p.className = 'auth-hint';
  p.textContent = message;
  const back = document.createElement('a');
  back.className = 'auth-submit';
  back.href = '/';
  back.textContent = 'Zur Projektübersicht';
  card.append(h1, p, back);
  root.appendChild(card);
}
